import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Clock, Signal, CalendarDays, Check, ArrowLeft } from "lucide-react";
import { Layout } from "../components/Layout";
import { LotusMark } from "../components/Logo";
import { getProgram, programs } from "../data/programs";
import { siteConfig } from "../data/siteConfig";

const NotFound = () => (
  <Layout>
    <section className="pt-40 pb-32 text-center">
      <div className="container mx-auto px-6 max-w-lg">
        <LotusMark className="w-12 h-12 mx-auto mb-6" />
        <h1 className="font-heading text-4xl text-charcoal mb-4">
          Program not found
        </h1>
        <p className="text-charcoal/70 mb-8">
          We couldn't find that program. Browse all our offerings instead.
        </p>
        <RouterLink
          to="/programs"
          className="inline-block px-7 py-3.5 bg-primary text-white rounded-full font-semibold shadow-soft hover:bg-primary-dark transition-all"
        >
          View all programs
        </RouterLink>
      </div>
    </section>
  </Layout>
);

export const ProgramDetailPage = () => {
  const { slug } = useParams();
  const program = getProgram(slug);

  if (!program) return <NotFound />;

  const related = programs.filter((p) => p.slug !== program.slug).slice(0, 3);

  const meta = [
    { icon: Clock, label: "Duration", value: program.duration },
    { icon: Signal, label: "Level", value: program.level },
    { icon: CalendarDays, label: "Schedule", value: program.schedule },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-12">
        <div className="container mx-auto px-6">
          <RouterLink
            to="/programs"
            className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-primary mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All programs
          </RouterLink>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-semibold text-charcoal mb-4">
                {program.title}
              </h1>
              <p className="text-lg text-charcoal/70 leading-relaxed">
                {program.summary}
              </p>
            </div>
            <div className="rounded-[2.5rem] overflow-hidden shadow-soft-lg border-4 border-white">
              <img
                src={program.image}
                alt={program.title}
                className="w-full h-72 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meta cards */}
      <section className="pb-4">
        <div className="container mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-5">
            {meta.map((m) => (
              <div
                key={m.label}
                className="bg-white rounded-2xl p-6 border border-accent/15 shadow-soft flex items-center gap-4"
              >
                <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <m.icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-widest text-charcoal/50">
                    {m.label}
                  </p>
                  <p className="font-medium text-charcoal">{m.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-16">
        <div className="container mx-auto px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="font-heading text-3xl text-charcoal mb-4">
              About this program
            </h2>
            <p className="text-charcoal/75 leading-relaxed text-lg mb-8">
              {program.description}
            </p>
            <h3 className="font-heading text-2xl text-charcoal mb-4">
              What you'll experience
            </h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {program.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-3 bg-white rounded-xl p-4 border border-accent/15"
                >
                  <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-charcoal/80">{h}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Booking sidebar */}
          <aside>
            <div className="bg-charcoal text-cream rounded-3xl p-8 sticky top-28">
              <LotusMark className="w-9 h-9 mb-4" stroke="#FBF8F1" />
              <h3 className="font-heading text-2xl text-white mb-2">
                Ready to begin?
              </h3>
              <p className="text-cream/75 text-sm mb-2">
                Pricing: {program.priceNote}.
              </p>
              <p className="text-cream/75 text-sm mb-6">
                See full pricing for your region, or message Suman directly to
                book your spot.
              </p>
              <a
                href={siteConfig.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center px-6 py-3 bg-white text-primary rounded-full font-semibold mb-3 hover:bg-cream-dark transition-all"
              >
                Book on WhatsApp
              </a>
              <RouterLink
                to="/pricing"
                className="block text-center px-6 py-3 bg-white/10 text-white rounded-full font-semibold mb-3 hover:bg-white/20 transition-all"
              >
                View pricing
              </RouterLink>
              <RouterLink
                to="/contact"
                className="block text-center px-6 py-3 border border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-all"
              >
                Contact us
              </RouterLink>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl text-charcoal mb-6">
            Other programs
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((p) => (
              <RouterLink
                key={p.slug}
                to={`/programs/${p.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-accent/15 shadow-soft hover:-translate-y-1 transition-all"
              >
                <div className="h-32 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-lg text-charcoal">
                    {p.title}
                  </h3>
                </div>
              </RouterLink>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProgramDetailPage;
